import styles from './all-contracts-grid.module.scss';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GetCurrentCompany } from '../../customer-context/customer-context';
import AllContractsDetailsGridRenderer from '../all-contracts-details-grid-renderer/all-contracts-details-grid-renderer';
import { ValueFormatterParams } from 'ag-grid-community';
import { UseSignalRHook } from '../../signalr-context/signalr-context';
import RightsWrapper from '../../rights-wrapper/rights-wrapper';
import { makeRequest, useTableConfigHook } from '../../requestUtils';
import { booleanHeader, CreateHeaders, currencyHeader, dateFormatter, dateHeader, HeaderSizeEnum, Icon, OrderTypesEnum, PrimaryButton, Roles, SumRows, textHeader } from '@frontend-workspace/react-shared-lib';
import { IAllContracts } from '../../_models/all-contracts';
import OrderOverviewDialog from '../order-overview-dialog/order-overview-dialog';
import MoveContractsDialog from '../move-contracts-dialog/move-contracts-dialog';
import MergeContractsDialog from '../merge-contracts-dialog/merge-contracts-dialog';
import WebkuaTable from '../../webkua-table/webkua-table';
import { useTranslation } from 'react-i18next';
import { GetRoles } from '../../TokenContext';
import { SalesforceRecordLink } from '../../salesforce-navigation/salesforce-navigation';

/* eslint-disable-next-line */
export interface AllContractsGridProps {
  salesforceBaseUrl?: string,
  allContracts: IAllContracts[],
  isGroup: boolean,
  onIsGroupChange: () => void,
  onlyActive: boolean,
  getData: () => void,
  onOnlyActiveChange: () => void,
  isLoading: boolean
}

export function AllContractsGrid(props: AllContractsGridProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tableConfig = useTableConfigHook("AllContracts");
  const currentCompany = GetCurrentCompany();
  const [orderOverview, setOrderOverview] = useState<boolean>(false);
  const [moveContracts, setMoveContracts] = useState<boolean>(false);
  const [mergeContracts, setMergeContracts] = useState<boolean>(false);

  UseSignalRHook(props.getData, [OrderTypesEnum.MoveContracts, OrderTypesEnum.CombineContracts]);
  const openOrderOverviewDialog = () => setOrderOverview(true);
  const closeOrderOverviewDialog = () => setOrderOverview(false);
  const openMoveContractsDialog = () => setMoveContracts(true);
  const closeMoveContractsDialog = () => setMoveContracts(false);
  const openMergeContractsDialog = () => setMergeContracts(true);
  const closeMergeContractsDialog = () => setMergeContracts(false);

  const userRoles = GetRoles();
  const handleNavigateToNewContract = () => { navigate(`/customers/${currentCompany?.number}/contracts/new`) }
  const handleNavigateToContractDetails = (contractNumber: string) => navigate(`/customers/${currentCompany?.number}/${"contracts"}/${contractNumber}`);

  const createProposalOnContract = (contractNumber: string) => {
    makeRequest(`proposals/action/startProposal/`, "POST", {
      companyNumber: currentCompany?.number,
      contractNumber: contractNumber,
      invoiceInterval: 12
    }).then(proposalId => {
      navigate(`/customers/${currentCompany?.number}/proposal/${proposalId}`);
    });
  };

  const toContractRenderer = (params: ValueFormatterParams) => {
    if (params.data.isSummary) return null;
    const isFutureContract = params.value === "Fremtidig";

    return (
      isFutureContract ? `Opprettes ${dateFormatter(params.data.nextInvoiceDate, true)}` :
        (userRoles.includes(Roles.Sales) && !userRoles.includes(Roles.Admin)) ?
          <PrimaryButton
            variant="outlined"
            size='small'
            onClick={() => createProposalOnContract(params.data.contractNumber)}>
            {t('shop.createProposal')}
          </PrimaryButton> :
          <PrimaryButton
            variant="outlined"
            size='small'
            onClick={() => handleNavigateToContractDetails(params.data.contractNumber)}>
            {t('standard.change')}
          </PrimaryButton>
    )
  };

  const salesforceBaseUrl = props.salesforceBaseUrl;
  const headers = CreateHeaders([
    textHeader("contractNumber", t('standard.contractNo'), HeaderSizeEnum.LARGE, {
      renderer: 'agGroupCellRenderer',
      rendererParams: salesforceBaseUrl ? {
        innerRenderer: ({ value, data }: { value?: string; data?: IAllContracts & { isSummary?: boolean } }) =>
          !data || data.isSummary || data.contractNumber === 'Fremtidig' ? value ?? null
            : <span className="d-inline-flex align-items-center gap-2">
                <span>{value}</span>
                <SalesforceRecordLink baseUrl={salesforceBaseUrl} recordType="Contract"
                  companyNumber={data.companyNumber ?? ''} contractNumber={data.contractNumber} />
              </span>,
      } : undefined,
      cellStyle: { fontSize: '0.875rem', fontWeight: '600' }
    }),
    textHeader("companyNumber", t('standard.companyNumber'), HeaderSizeEnum.MEDIUM, { hide: !props.isGroup }),
    textHeader("contractTypeName", t('standard.type'), HeaderSizeEnum.LARGE),
    textHeader("contractResponsible", t('standard.contractManager'), HeaderSizeEnum.LARGE),
    textHeader("invoiceResponsible", t('allContractsGrid.invoiceResponsible'), HeaderSizeEnum.LARGE),
    booleanHeader("isActive", t('standard.active'), { hide: props.onlyActive }),
    textHeader("numberOfLicenses", t('standard.licenses'), HeaderSizeEnum.LARGE, {
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header",
      summary: SumRows
    }),
    textHeader("numberOfSeats", t('standard.seats'), HeaderSizeEnum.MEDIUM, {
      hide: true,
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header",
      summary: SumRows
    }),
    booleanHeader("hasFutureChanges", t('allContractsGrid.futureChanges')),
    textHeader("invoiceInterval", t('allContractsGrid.invoiceInterval'), HeaderSizeEnum.LARGE, {
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    dateHeader("nextInvoiceDate", t('standard.nextInvoiceDate')),
    currencyHeader("nextInvoiceCost", t('standard.invoiceCost'), HeaderSizeEnum.LARGE, {
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header",
      summary: SumRows
    }),
    textHeader("contractNumber", "", HeaderSizeEnum.LARGE, {
      renderer: toContractRenderer,
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    })
  ]);

  if (!currentCompany) return null;
  return <>
    <RightsWrapper>
      <div className="d-flex flex-row align-items-center flex-wrap mb-3 mb-xl-0">
        {/* <div className='mb-3 mb-md-0 me-3 me-md-4'>
          {
            (userRoles.includes(Roles.Sales) && !userRoles.includes(Roles.Admin)) ?
              <PrimaryButton
                variant="contained"
                onClick={() => createProposalOnContract('NY')}>
                <Icon className="me-1" fontSize="small">add</Icon>{t('standard.newOffer')}
              </PrimaryButton> :
              <PrimaryButton
                variant="contained"
                onClick={handleNavigateToNewContract}>
                <Icon className="me-1" fontSize="small">add</Icon>{t('standard.newContract')}
              </PrimaryButton>
          }
        </div> */}
        <div className='mb-3 mb-md-0 me-3 me-md-4'>
          <PrimaryButton
            variant="outlined"
            onClick={openOrderOverviewDialog}>
            {t('standard.orderOverview')}
          </PrimaryButton>
        </div>
        {/* <div className='mb-3 mb-md-0 me-3 me-md-4'>
          <PrimaryButton
            variant="outlined"
            onClick={openMoveContractsDialog}>
            {t('standard.moveContracts')}
          </PrimaryButton>
        </div>
        <div className='mb-3 mb-md-0'>
          <PrimaryButton
            variant="outlined"
            onClick={openMergeContractsDialog}>
            {t('standard.mergeContracts')}
          </PrimaryButton>
        </div> */}
      </div>
    </RightsWrapper>
    <WebkuaTable
      isLoading={props.isLoading}
      data={props.allContracts}
      headers={headers}
      tableConfig={tableConfig}
      expandComponent={AllContractsDetailsGridRenderer}
      tableFilters={[{
        label: t('standard.showForGroup'),
        checked: props.isGroup,
        onChange: props.onIsGroupChange
      }, {
        label: t('standard.activeOnly'),
        checked: props.onlyActive,
        onChange: props.onOnlyActiveChange
      }]}
    />
    {orderOverview && <OrderOverviewDialog
      open={!!orderOverview}
      onClose={closeOrderOverviewDialog}
    />}
    {!!moveContracts && <MoveContractsDialog
      open={!!moveContracts}
      onClose={closeMoveContractsDialog}
      contracts={props.allContracts}
    />}
    {!!mergeContracts && <MergeContractsDialog
      open={!!mergeContracts}
      onClose={closeMergeContractsDialog}
      allContracts={props.allContracts}
    />}
  </>;
}

export default AllContractsGrid;